import Growth from "@/assets/icons/Growth.svg";
import Smile from "@/assets/icons/RedditCardSmile.svg";
import { TMarketScriptCard } from "./MarketScriptCard";
import EvidenceCommentsDisplay from "./EvidenceCommentsDisplay";

const MarketScriptDetailsModal = ({ data }: { data: TMarketScriptCard }) => (
  <div className="max-w-4xl mx-auto">
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{data.title}</h2>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-lg border">
          <span className="text-sm text-gray-600">Script Sourced from</span>
          <span className="text-sm font-semibold text-gray-900">Reddit</span>
          <Smile />
        </div>
        {data.engagement_metric && (
          <div className="bg-green-50 rounded-lg py-1 px-3 flex items-center gap-1 border border-green-200">
            <Growth />
            <span className="text-green-800 text-sm font-semibold">
              {Math.round(data.engagement_metric)}% engagement
            </span>
          </div>
        )}
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Script Content */}
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-lg p-4 border">
          <h3 className="font-semibold text-gray-900 mb-2">Hook</h3>
          <p className="text-gray-700 leading-relaxed">{data.hook || "Not specified"}</p>
        </div>

        <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Voiceover Script</h3>
          <p className="text-gray-700 text-sm leading-relaxed">
            {data.voiceover_script}
          </p>
        </div>
      </div>

      {/* Additional Details */}
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-lg p-4 border">
          <h3 className="font-semibold text-gray-900 mb-2">Visuals</h3>
          <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
            {data.main_content}
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Call to Action</h3>
          <p className="text-gray-700 text-sm leading-relaxed">{data.call_to_action}</p>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 border-t border-b border-gray-300 py-10">
      <div className="bg-gray-50 rounded-lg p-4 border">
        <h3 className="font-semibold text-gray-900 mb-2">Target Tags</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="font-medium text-gray-600">Hierarchy:</span>{" "}
            <span className="text-gray-900">{data.target_tags.hierarchy}</span>
          </div>
          <div>
            <span className="font-medium text-gray-600">Storyline:</span>{" "}
            <span className="text-gray-900">{data.target_tags.storyline}</span>
          </div>
          <div>
            <span className="font-medium text-gray-600">Hook:</span>{" "}
            <span className="text-gray-900">{data.target_tags.hook}</span>
          </div>
          <div>
            <span className="font-medium text-gray-600">CTA:</span>{" "}
            <span className="text-gray-900">{data.target_tags.cta}</span>
          </div>
          <div>
            <span className="font-medium text-gray-600">Actor:</span>{" "}
            <span className="text-gray-900">{data.target_tags.actor}</span>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-2">Pain Points Addressed</h3>
        <ul className="text-gray-700 text-sm space-y-1">
          {data.pain_points_addressed.map((point, index) => (
            <li key={index} className="flex items-start leading-relaxed">
              <span className="text-gray-400 mr-2 mt-0.5">•</span>
              {point}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-2">Desires Fulfilled</h3>
        <ul className="text-gray-700 text-sm space-y-1">
          {data.desires_fulfilled.map((desire, index) => (
            <li key={index} className="flex items-start leading-relaxed">
              <span className="text-gray-400 mr-2 mt-0.5">•</span>
              {desire}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-2">Habits Leveraged</h3>
        <ul className="text-gray-700 text-sm space-y-1">
          {data.habits_leveraged.map((habit, index) => (
            <li key={index} className="flex items-start leading-relaxed">
              <span className="text-gray-400 mr-2 mt-0.5">•</span>
              {habit}
            </li>
          ))}
        </ul>
      </div>
    </div>
    
    <EvidenceCommentsDisplay evidenceComments={data.evidence_comments} />
  </div>
);

export default MarketScriptDetailsModal;